import { forwardRef, useRef, useState, useEffect, useCallback, ChangeEvent } from 'react';
import mergeRefs from 'react-merge-refs';
import { createTextMaskInputElement, type IMaskConfig } from 'text-mask-core';
import { Input, type CommonInputProps } from 'src/shared/ui/Input';

export type CommonMaskedInputProps = CommonInputProps & {
  mask?: IMaskConfig['mask'];
  keepCharPositions?: IMaskConfig['keepCharPositions'];
  onBeforeDisplay?: IMaskConfig['pipe'];
};

/* TODO: Добавить обработку clear */

export const MaskedInput = forwardRef<HTMLInputElement, CommonMaskedInputProps>(
  (
    { mask, keepCharPositions, className, value, defaultValue, onChange, onBeforeDisplay, onClear, ...restProps },
    ref,
  ) => {
    const [inputNode, setInputNode] = useState<HTMLInputElement | null>(null);
    const textMask = useRef<ReturnType<typeof createTextMaskInputElement> | null>(null);

    const [inputValue, setInputValue] = useState(value || defaultValue || '');

    const update = useCallback(
      (newValue: string = '') => {
        if (textMask.current && inputNode) {
          textMask.current.update(newValue);

          setInputValue(inputNode.value);
        }
      },
      [inputNode],
    );

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        update(event.target.value);

        if (onChange) {
          onChange(event, { value: event.target.value });
        }
      },
      [onChange, update],
    );

    useEffect(() => {
      if (inputNode) {
        textMask.current = createTextMaskInputElement({
          mask,
          inputElement: inputNode,
          pipe: onBeforeDisplay,
          guide: false,
          keepCharPositions,
          showMask: false,
          rawValue: '',
          currentCaretPosition: 0,
          previousConformedValue: '',
        });
      }
    }, [mask, keepCharPositions, inputNode, onBeforeDisplay]);

    useEffect(() => {
      update(value || defaultValue);
    }, [value, defaultValue, update]);

    return (
      <Input
        {...restProps}
        ref={mergeRefs([ref, setInputNode])}
        className={className}
        value={inputValue}
        onChange={handleChange}
      />
    );
  },
);

MaskedInput.displayName = 'MaskedInput';
